import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import NewOrderHeader from "./NewOrderHeader";
import useCart from "./useCart";
import useAddresses from "./useAddresses";
import usePaymentMethods from "./usePaymentMethods";
import useNewOrderState, { maxStep } from "./useNewOrderState";
import AddressesSection from "./AddressesSection";
import AddressEditor from "@/components/AddressEditor";
import { inputCls } from "./styles";

/**
 * Step 2 — Dados de Entrega: facturação, envio, forma de envio + observações.
 *
 * Sincroniza as selecções com o `useNewOrderState` para que persistam até à
 * página de pagamento.
 */
export default function NewOrderCheckoutPage() {
  const navigate = useNavigate();
  const { activeId } = useAuth();
  const [state, update] = useNewOrderState();

  const cartH = useCart();
  const addr = useAddresses();
  const pay = usePaymentMethods();

  const [observacoes, setObservacoes] = useState(state.observacoes || "");
  const [addressEditor, setAddressEditor] = useState(null);

  // Carrinho vazio → redirect ao step 1.
  useEffect(() => {
    if (!cartH.loading && cartH.cart.length === 0) {
      navigate("/encomendas/nova/carrinho", { replace: true });
    }
  }, [cartH.loading, cartH.cart.length, navigate]);

  // Restaurar selecções do state persistido quando os hooks carregam.
  useEffect(() => {
    if (state.selectedFatura && !addr.selectedFatura) addr.setSelectedFatura(state.selectedFatura);
    if (state.selectedEnvio && !addr.selectedEnvio) addr.setSelectedEnvio(state.selectedEnvio);
    if (state.selectedTransport && !pay.selectedTransport) pay.setSelectedTransport(state.selectedTransport);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addr.addresses.length, pay.transportMethods.length]);

  // Recarregar quando muda cliente alvo (X-View-As).
  useEffect(() => {
    cartH.load?.();
    addr.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const goBack = () => navigate("/encomendas/nova/carrinho");

  const goNext = () => {
    if (!addr.selectedFatura) {
      window.alert("Seleccione uma morada de facturação.");
      return;
    }
    if (!pay.selectedTransport) {
      window.alert("Seleccione uma forma de envio.");
      return;
    }
    if (!addr.selectedEnvio) {
      window.alert("Seleccione uma morada de envio.");
      return;
    }
    update({
      selectedFatura: addr.selectedFatura,
      selectedEnvio: addr.selectedEnvio,
      selectedTransport: pay.selectedTransport,
      observacoes,
      maxStep: maxStep(state.maxStep, "payment"),
    });
    navigate("/encomendas/nova/pagamento");
  };

  return (
    <div data-testid="new-order-checkout-page">
      <NewOrderHeader maxStepReached={maxStep(state.maxStep, "checkout")} />

      <div className="mb-4">
        <h1 className="text-2xl font-bold text-[#111827]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 600 }}>Dados de Entrega</h1>
        <p className="text-sm text-[#6B7280] mt-1">Confirme as moradas de facturação e envio + a forma de envio.</p>
      </div>

      <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm p-5 space-y-4">
        <AddressesSection
          addresses={addr.addresses} billingAddresses={addr.billingAddresses}
          selectedFatura={addr.selectedFatura} setSelectedFatura={addr.setSelectedFatura}
          selectedTransport={pay.selectedTransport} setSelectedTransport={pay.setSelectedTransport}
          transportMethods={pay.transportMethods}
          selectedEnvio={addr.selectedEnvio} setSelectedEnvio={addr.setSelectedEnvio}
          onOpenEditor={setAddressEditor}
        />

        <div>
          <label className="block text-xs font-medium text-[#6B7280] mb-1">Observações</label>
          <textarea data-testid="order-notes" value={observacoes}
            onChange={(e) => setObservacoes(e.target.value)}
            rows={3} placeholder="Observações (opcional)"
            className={`${inputCls} resize-y`} />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm p-5 flex items-center justify-between gap-4 flex-wrap mt-4">
        <button type="button" onClick={goBack}
          data-testid="new-order-checkout-back"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-[#E5E7EB] text-[#374151] text-sm rounded-md hover:bg-[#F9FAFB] transition-colors">
          <ArrowLeft size={16} /> Voltar ao Carrinho
        </button>
        <button type="button" onClick={goNext}
          data-testid="new-order-checkout-next"
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#0F1829] text-white text-sm font-semibold rounded-md hover:bg-[#001f6e] transition-colors">
          Avançar para Pagamento <ArrowRight size={16} weight="bold" />
        </button>
      </div>

      {addressEditor && (
        <AddressEditor
          mode={addressEditor.mode}
          tipoMorada={addressEditor.tipoMorada}
          address={addressEditor.address}
          onClose={() => setAddressEditor(null)}
          onSaved={(r) => {
            const newId = r?.ID_ClienteMorada || addressEditor.address?.ID_ClienteMorada;
            addr.reload(newId, addressEditor.target);
          }}
        />
      )}
    </div>
  );
}
